import { useEffect, useRef, useMemo, ReactNode, ComponentProps, FocusEvent } from "react";
import { LitMenu, MdMenu } from "../Menu/LitMenu";
import { ReactWebComponent } from "@lit/react";
import classNames from "classnames";

type LitMenuProps = Pick<ComponentProps<ReactWebComponent<MdMenu, {}>>, "positioning" | "anchorCorner" | "menuCorner">;

export type MenuProps = LitMenuProps & {
  AnchorComponent: ReactNode;
  content: ReactNode;
  variant: "rich" | "plain";
  color: "light" | "dark";
  anchorClassName?: string;
  noVerticalFlip?: boolean;
  closeDelay?: number;
  compactHoverArea?: boolean;
  compactTopPadding?: boolean;
  focusableAnchor?: boolean;
};

const sizeClasses = {
  plain: "px-2 w-max max-w-[400px]",
  rich: "px-4 pb-4 w-[312px]"
};

const containerColors = {
  light: "var(--md-sys-color-surface)",
  dark: "var(--md-sys-color-on-surface)"
};

const textColorClasses = {
  light: "text-sys-brand-on-surface",
  dark: "text-sys-brand-surface"
};

const randomNum = () => (Math.random() * 10000000).toFixed(0);

export default function MenuTooltip({
  content,
  AnchorComponent,
  variant,
  color,
  anchorCorner: anchorCornerProp,
  menuCorner: menuCornerProp,
  positioning = "absolute",
  anchorClassName,
  noVerticalFlip,
  closeDelay = 0,
  compactHoverArea = false,
  compactTopPadding = false,
  focusableAnchor = false
}: MenuProps) {
  const anchorCorner = anchorCornerProp ?? (variant === "plain" ? "start-start" : "end-end");
  const menuCorner = menuCornerProp ?? (variant === "plain" ? "end-start" : "start-end");
  const menuRef = useRef<MdMenu>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    menuRef.current?.show();
  };

  const hideTooltip = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (closeDelay > 0) {
      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null;
        menuRef.current?.close();
      }, closeDelay);
    } else {
      menuRef.current?.close();
    }
  };

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    },
    []
  );

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && (event.currentTarget.contains(next) || menuRef.current?.contains(next))) {
      return;
    }
    hideTooltip();
  };

  const handleMouseLeave = () => {
    if (wrapperRef.current?.contains(document.activeElement)) return;
    hideTooltip();
  };

  const anchorId = useMemo(() => `menu-tooltip-anchor-${randomNum()}`, []);
  const contentId = `${anchorId}-content`;

  const style = {
    "--md-menu-container-shape": variant === "plain" ? "4px" : "12px",
    "--md-menu-container-color": containerColors[color],
    minWidth: "60px"
  } as React.CSSProperties;

  const defaultHoverAreaClass = compactHoverArea ? "md:-mx-2 md:px-2" : "md:-mx-10 md:px-10";

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={handleMouseLeave}
      onFocus={showTooltip}
      onBlur={handleBlur}
      data-testid="menu-tooltip"
    >
      {/* Adding the spacing to allow for larger hover area */}
      <div
        id={anchorId}
        role={focusableAnchor ? undefined : "button"}
        tabIndex={focusableAnchor ? 0 : undefined}
        aria-describedby={focusableAnchor ? contentId : undefined}
        className={classNames(
          "flex items-center",
          anchorClassName || defaultHoverAreaClass,
          focusableAnchor &&
            "rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary"
        )}
      >
        {!!AnchorComponent && AnchorComponent}
      </div>
      <LitMenu
        quick
        anchor={anchorId}
        ref={menuRef}
        {...{ style, anchorCorner, menuCorner, positioning, noVerticalFlip }}
      >
        <div
          role="tooltip"
          id={contentId}
          className={classNames(
            sizeClasses[variant],
            variant === "rich" && (compactTopPadding ? "pt-2" : "pt-4"),
            textColorClasses[color],
            "whitespace-pre-line cursor-default"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>
      </LitMenu>
    </div>
  );
}
